import { getMessaging, getToken, isSupported } from 'firebase/messaging'
import { app, FIREBASE_VAPID_KEY } from '@/config/firebase'
import { clearFcmRegistrationToken, setFcmRegistrationToken } from '@/services/session'

export type NotificationGateFailureReason = 'insecure' | 'unsupported' | 'denied' | 'dismissed'

export type NotificationGateResult =
  | { ok: true }
  | { ok: false; reason: NotificationGateFailureReason }

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

/**
 * Asks the browser to show the notifications permission prompt when still "default".
 * Call this before turning notifications on in Settings.
 */
export async function ensureBrowserNotificationPermission(): Promise<NotificationGateResult> {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'unsupported' }
  }
  if (!window.isSecureContext && !isLocalDevHost()) {
    return { ok: false, reason: 'insecure' }
  }
  if (!('Notification' in window)) {
    return { ok: false, reason: 'unsupported' }
  }

  let perm = Notification.permission
  if (perm === 'default') {
    perm = await Notification.requestPermission()
  }

  if (perm === 'granted') {
    return { ok: true }
  }
  if (perm === 'denied') {
    return { ok: false, reason: 'denied' }
  }
  return { ok: false, reason: 'dismissed' }
}

/** Use console.warn so Safari’s default console level still shows why localStorage has no fcmRegistrationToken. */
function logFcm(message: string, detail?: unknown) {
  if (detail !== undefined) console.warn('[FCM]', message, detail)
  else console.warn('[FCM]', message)
}

/**
 * Registers the FCM service worker and returns the device token when VAPID is configured.
 * Safe to ignore failures — browser permission can still be granted without a token.
 */
export async function obtainFcmRegistrationToken(): Promise<string | null> {
  const vapid = FIREBASE_VAPID_KEY.trim()
  if (!vapid) {
    logFcm(
      'No device token: VITE_FIREBASE_VAPID_KEY is missing or empty in .env.',
      'Firebase Console → Project settings → Cloud Messaging → Web Push certificates → Key pair → copy into .env, then restart `npm run dev`.'
    )
    return null
  }

  const supported = await isSupported().catch(() => false)
  if (!supported) {
    logFcm(
      'No device token: Firebase says messaging is not supported in this browser (isSupported() is false).',
      'Try Google Chrome on desktop, or a recent macOS Safari with HTTPS. Many Safari builds never store an FCM web token.'
    )
    return null
  }

  if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
    logFcm(
      'No device token: notification permission is denied for this site.',
      'Safari → Settings → Websites → Notifications → set this site to Allow, or clear site data and try again.'
    )
    return null
  }

  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    logFcm(
      'Requesting notification permission (required before getToken).',
      'If no dialog appears, check Safari → Websites → Notifications for this origin.'
    )
    const perm = await Notification.requestPermission()
    if (perm !== 'granted') {
      logFcm('No device token: notification permission was not granted.', String(perm))
      return null
    }
  }

  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    })
    await navigator.serviceWorker.ready
    const messaging = getMessaging(app)
    const token = await getToken(messaging, {
      vapidKey: vapid,
      serviceWorkerRegistration: registration,
    })
    if (token) {
      if (import.meta.env.DEV) console.info('[FCM] Device token received; app will store it as localStorage fcmRegistrationToken.')
    } else {
      logFcm(
        'getToken returned empty.',
        'Open /firebase-messaging-sw.js in the address bar — if 404, restart dev server with a full .env (Vite generates that file).'
      )
    }
    return token || null
  } catch (e) {
    logFcm(
      'Service worker registration or getToken threw an error.',
      'Check that public/firebase-messaging-sw.js exists after starting Vite, and that VAPID matches your Firebase project.'
    )
    console.warn('[FCM] Error detail:', e)
    return null
  }
}

/**
 * Clears any stored FCM token, obtains a fresh one (if VAPID + permission allow), and saves it.
 * Call after login / signup / social auth so each session uses a current device token.
 */
export async function refreshAndStoreFcmToken(): Promise<void> {
  clearFcmRegistrationToken()
  const token = await obtainFcmRegistrationToken()
  if (token) {
    setFcmRegistrationToken(token)
  }
}
